import { useRef, useEffect, useState } from 'react';
import { Eraser, Check, X as XIcon } from '@phosphor-icons/react';

/**
 * Touch-friendly handtekening-canvas.
 * props:
 *   width, height: canvas afmetingen (default 600x200)
 *   onSave({ dataUrl, naam }): callback bij opslaan
 *   onCancel(): optioneel
 *   initialName: voor klantnaam invulveld
 */
export default function SignaturePad({ width = 600, height = 200, onSave, onCancel, initialName = '' }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasInk, setHasInk] = useState(false);
  const [naam, setNaam] = useState(initialName);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111';
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return {
      x: (t.clientX - rect.left) * (canvasRef.current.width / rect.width),
      y: (t.clientY - rect.top) * (canvasRef.current.height / rect.height),
    };
  };

  const start = (e) => {
    e.preventDefault();
    setDrawing(true);
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasInk(true);
  };

  const end = () => setDrawing(false);

  const wis = () => {
    const c = canvasRef.current;
    c.getContext('2d').clearRect(0, 0, c.width, c.height);
    setHasInk(false);
  };

  const save = () => {
    if (!hasInk) { alert('Gelieve eerst te tekenen.'); return; }
    if (!naam.trim()) { alert('Gelieve uw naam in te vullen.'); return; }
    const dataUrl = canvasRef.current.toDataURL('image/png');
    onSave?.({ dataUrl, naam: naam.trim() });
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white" data-testid="signature-pad">
      <div className="px-4 py-2 border-b border-gray-100 flex items-center justify-between" style={{ background: '#F9FAFB' }}>
        <span className="text-xs font-bold uppercase tracking-wider text-gray-600">Voor ontvangst getekend</span>
        <button type="button" className="text-xs text-gray-500 hover:text-gray-800 inline-flex items-center gap-1" onClick={wis} data-testid="sig-wis"><Eraser size={12} /> Wissen</button>
      </div>
      <div className="p-4 space-y-3">
        <div>
          <label className="form-label">Naam van de ontvanger</label>
          <input
            className="field-input"
            value={naam}
            onChange={e => setNaam(e.target.value)}
            placeholder="Voor- en achternaam"
            style={{ minHeight: 40 }}
            data-testid="sig-naam"
          />
        </div>
        <div>
          <label className="form-label">Handtekening</label>
          <div className="rounded border-2 border-dashed border-gray-300 bg-white overflow-hidden" style={{ touchAction: 'none' }}>
            <canvas
              ref={canvasRef}
              width={width}
              height={height}
              className="w-full cursor-crosshair"
              style={{ height, maxHeight: height, display: 'block', touchAction: 'none' }}
              onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
              onTouchStart={start} onTouchMove={move} onTouchEnd={end}
              data-testid="sig-canvas"
            />
          </div>
          <p className="text-[10px] text-gray-400 mt-1">Teken met vinger of stylus.</p>
        </div>
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
          {onCancel && (
            <button type="button" className="btn btn-ghost" onClick={onCancel} data-testid="sig-cancel">
              <XIcon size={14} /> Annuleren
            </button>
          )}
          <button type="button" className="btn btn-success" onClick={save} disabled={!hasInk} data-testid="sig-save">
            <Check size={14} /> Bevestigen
          </button>
        </div>
      </div>
    </div>
  );
}
